import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import { Button, IconButton, TextField } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import InputTextField from 'components/inputfields/InputTextField';
import InputLabel from 'components/inputfields/InputLabel';
import { APPLICATION_ROLES } from 'components/constants';
import CircularSpinner from 'components/loadingIndicator/CircularSpinner';
import IProduct from 'models/product';
import IProducer from 'models/producer';
import { isBlank } from 'utils/helpers-ts';
import { useDebouncedStateString } from 'hooks/useDebouncedState';

import { isCategoryDimmed, isNewNonFood } from './category';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { IFoodlaCategoryOption } from 'models/category';
import { FieldHistoryIcon } from '../FieldHistoryIcon';
import { OrgCategoryDisplay } from './category/org-category-display';

const useStyles = makeStyles({
  exportContainer: {
    display: 'flex',
    flexDirection: 'column',
    lineHeight: '26px',
  },
  exportWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
});

interface ManualExportTitleProps {
  dim?: boolean;
  readOnly: boolean;
  label: string;
  inputLabel: string;
  initialValue: string;
  field: 'icaExportTitle' | 'apoteaExportTitle';
  state: IProduct;
  setState: Dispatch<SetStateAction<IProduct>>;
}

const ManualExportTitle: FC<ManualExportTitleProps> = ({
  dim,
  readOnly,
  label,
  inputLabel,
  initialValue,
  field,
  state,
  setState,
}) => {
  const value = state[field];

  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  const handleCancel = () => {
    setEditing(false);
    setLocalValue(value);
  };

  const handleUpdate = () => {
    setEditing(false);
    setState((oldState) => ({ ...oldState, [field]: localValue }));
  };

  const handleClear = () => {
    setEditing(false);
    setState((oldState) => ({ ...oldState, [field]: '' }));
  };

  useEffect(() => {
    setLocalValue(initialValue);
  }, [editing, initialValue]);

  return (
    <div style={{ marginBottom: editing ? 8 : 0 }}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <OrgCategoryDisplay label={label} value={value} dim={dim} />

        {!editing && !readOnly && (
          <IconButton aria-label={`Redigera ${label}`} size="small" style={{ color: '#000' }} onClick={() => setEditing(x => !x)}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {editing && (
        <div style={{ marginTop: 8 }}>
          <TextField
            id={field}
            fullWidth
            variant="outlined"
            label={inputLabel}
            value={localValue}
            onChange={(event) => setLocalValue(event.target.value)}
          />
          <div style={{ marginTop: 4 }}>
            <Button disabled={readOnly} size="small" variant="text" color="primary" style={{ marginRight: 8 }} onClick={handleCancel}>
              Avbryt
            </Button>
            <Button disabled={readOnly} size="small" variant="text" color="primary" style={{ marginRight: 8 }} onClick={handleClear}>
              Rensa
            </Button>
            <Button disabled={readOnly || isBlank(localValue)} size="small" variant="outlined" color="primary" onClick={handleUpdate}>
              Uppdatera namn
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

interface ITitleProps {
  readOnly?: boolean;
  producerData?: IProducer;
  isCreatingNew: boolean;
  state: IProduct;
  setState: (state: IProduct | ((prevState: IProduct) => IProduct)) => void;
  userRole?: string;
  approve?: boolean;
  icaExportData?: any;
  icaGeneratedNameData?: any;
  woltExportData?: any;
  coopExportData?: any;
  apoteaExportData?: any;
  apoteaGeneratedNameData?: any;
  rootCategory?: IFoodlaCategoryOption;
}

export const Title = ({
  readOnly,
  producerData,
  isCreatingNew,
  userRole,
  state,
  setState,
  approve,
  icaExportData,
  icaGeneratedNameData,
  woltExportData,
  coopExportData,
  apoteaExportData,
  apoteaGeneratedNameData,
  rootCategory,
}: ITitleProps) => {
  const classes = useStyles();
  const [valueBeforeBlur, setValueBeforeBlur] = useState(state.title || '');
  const [focused, setFocused] = useState(false);

  // Use debounced state hook for immediate UI feedback with debounced parent updates
  const [value, handleDebouncedChange] = useDebouncedStateString(state.title || '', setState, 'title', 300);

  const isNonFood = isNewNonFood(state.foodlaCategory, rootCategory);
  const isSuperuser = userRole === APPLICATION_ROLES.SUPERUSER;
  const isProducerOrStore = userRole === APPLICATION_ROLES.PRODUCER || userRole === APPLICATION_ROLES.STORE;
  const isIcaEnabled = !!producerData?.organizations?.includes('ica');
  const isApoteaEnabled = !!producerData?.organizations?.includes('apotea');
  const isCoopEnabled = !producerData?.organizations || !!producerData?.organizations?.includes('coop');
  const showIcaTitle = isSuperuser || (isProducerOrStore && isIcaEnabled);
  const showCoopTitle = isSuperuser || (isProducerOrStore && isCoopEnabled);
  const showApoteaTitle = isSuperuser || (isProducerOrStore && isApoteaEnabled);
  const hasIcaOverride = isSuperuser && !isBlank(state.icaExportTitle);
  const hasApoteaOverride = isSuperuser && !isBlank(state.apoteaExportTitle);
  // With an override the main previews return the manual name, so show the generated one
  // from the preview that was requested without overrides.
  const icaGeneratedData = hasIcaOverride ? icaGeneratedNameData : icaExportData;
  const apoteaGeneratedData = hasApoteaOverride ? apoteaGeneratedNameData : apoteaExportData;

  const requireError = useMemo(() => {
    if (isCreatingNew && !focused && !state.isDraft) {
      return '';
    }

    if (isBlank(state.title)) {
      return 'Vänligen fyll i detta fält eller spara som utkast.';
    }
    return '';
  }, [focused, state, isCreatingNew]);

  const labelOptionsFood = {
    heading: 'Produktnamn',
    text: 'Namn som beskriver produkten och kommer att visas för konsumenterna.',
    link: 'https://kontrollwiki.livsmedelsverket.se/artikel/40/livsmedlets-beteckning',
    required: true,
  };

  const labelOptionsNonFood = {
    heading: 'Produktnamn / Beteckning',
    text: 'Namn som beskriver vad det är för slags produkt.',
    required: true,
  };

  const handleBlur = () => {
    let titleValue = value;
    if (!state.disableCapitalizeTitle) {
      if (value.toLowerCase() !== valueBeforeBlur.toLowerCase()) {
        titleValue = value
          .split(' ')
          .map(text => (text[0] || '').toUpperCase() + (text.slice(1) || '').toLowerCase())
          .join(' ');
      }
    }
    handleDebouncedChange(titleValue);
    setValueBeforeBlur(titleValue);
  };

  useEffect(() => {
    setValueBeforeBlur(state.title || '');
  }, [state.title]);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', margin: '16px 0px 4px 0px' }}>
        {isSuperuser && <FieldHistoryIcon name="title" />}
        <InputLabel
          heading={isNonFood ? labelOptionsNonFood.heading : labelOptionsFood.heading}
          text={isNonFood ? labelOptionsNonFood.text : labelOptionsFood.text}
          link={isNonFood ? undefined : labelOptionsFood.link}
          required={true}
          labelStyles={{ margin: '0px 24px 0px 0px' }}
        />
      </div>
      <InputTextField
        readOnly={readOnly}
        hideComponentLabel={readOnly}
        inputName="title"
        value={value}
        label={isNonFood ? 'Ex. Tandkräm Fluor Mint' : 'Ex. Havredryck Osötad Naturell'}
        onChange={event => {
          const newValue = event.target.value || '';
          handleDebouncedChange(newValue);
        }}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        withLabel={false}
        disabled={state?.isMagazine}
        setState={setState}
        approve={isSuperuser && approve}
        state={state}
        inputClassName="data-hj-allow"
        labelOptions={isNonFood ? labelOptionsNonFood : labelOptionsFood}
        forceRed={!!requireError}
        errorText={requireError}
        helpText="Obligatoriskt för att aktivera produkten, men kan sparas som utkast utan att fyllas i."
        // ignoreTabs={true}
      />

      {isSuperuser && (
        <FormControlLabel
          disabled={readOnly}
          control={
            <Checkbox
              disabled={readOnly}
              size="small"
              checked={state.disableCapitalizeTitle}
              onChange={() => setState(state => ({ ...state, disableCapitalizeTitle: !state.disableCapitalizeTitle }))}
              color="primary"
            />
          }
          label="Bevara exakt formatering (inaktivera automatisk versalisering)"
          style={{ marginLeft: 0, marginBottom: 12 }}
        />
      )}

      {!!showIcaTitle && (
        <div className={classes.exportContainer}>
          <OrgCategoryDisplay
            loading={icaGeneratedData?.isLoading}
            label="ICA export:"
            value={icaGeneratedData?.data?.ItemDisplayName || ''}
            error={icaGeneratedData?.error}
            strikethrough={hasIcaOverride}
            dim={isCategoryDimmed(state, 'ica')}
          />
          {isSuperuser && (
            <ManualExportTitle
              dim={isCategoryDimmed(state, 'ica')}
              readOnly={!!readOnly}
              label='ICA export (manuell):'
              inputLabel='Produktnamn för ICA-export'
              field='icaExportTitle'
              initialValue={state.icaExportTitle || icaGeneratedData?.data?.ItemDisplayName || ''}
              state={state}
              setState={setState}
            />
          )}
        </div>
      )}
      {!!showCoopTitle && (
        <div className={classes.exportContainer}>
          <div
            className={classes.exportWrapper}
            style={{ opacity: isCategoryDimmed(state, 'coop') ? 0.5 : 1 }}
          >
            <b>Coop export:</b>
            {coopExportData?.isLoading ? (
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <CircularSpinner size={14} />
                <span>Hämtar data</span>
              </div>
            ) : (
              <span style={{ marginLeft: 3, color: coopExportData?.error ? 'red' : 'inherit' }}>
                {coopExportData?.error || coopExportData?.data?.title || ''}
              </span>
            )}
          </div>
        </div>
      )}
      {!!showApoteaTitle && (
        <div className={classes.exportContainer}>
          <OrgCategoryDisplay
            loading={apoteaGeneratedData?.isLoading}
            label="Apotea export:"
            value={apoteaGeneratedData?.data?.['A.Name'] || ''}
            error={apoteaGeneratedData?.error}
            strikethrough={hasApoteaOverride}
            dim={isCategoryDimmed(state, 'apotea')}
          />
          {isSuperuser && (
            <ManualExportTitle
              dim={isCategoryDimmed(state, 'apotea')}
              readOnly={!!readOnly}
              label='Apotea export (manuell):'
              inputLabel='Produktnamn för Apotea-export'
              field='apoteaExportTitle'
              initialValue={state.apoteaExportTitle || apoteaGeneratedData?.data?.['A.Name'] || ''}
              state={state}
              setState={setState}
            />
          )}
        </div>
      )}
      {isSuperuser && (
        <div className={classes.exportContainer}>
          <div
            className={classes.exportWrapper}
            style={{ opacity: isCategoryDimmed(state, 'wolt') ? 0.5 : 1 }}
          >
            <b>Wolt export:</b>
            {woltExportData?.isLoading ? (
              <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <CircularSpinner size={14} />
                <span>Hämtar data</span>
              </div>
            ) : (
              <span style={{ marginLeft: 3, color: woltExportData?.error ? 'red' : 'inherit' }}>
                {woltExportData?.error || woltExportData?.data?.name || ''}
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Title;
